import {
  KoButton,
  KoDataTableEdit,
  KoForm,
  KoInput,
  KoLoader,
  KoMonaco,
  KoNano,
  KoSelect,
  KoTable,
  KoTabs,
  KoText,

  KoToast,
  KoEmpty
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { HTTP_METHOD_OPTIONS, HTTP_METHOD_CONFIG, AUTH_TYPE_OPTIONS } from '../constants';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_SAVED_REQUESTS = [
  { _id: 'req-001', name: 'Get All Users', method: 'GET', url: 'https://api.kwapio.com/v3/users', authType: 'bearer' },
  { _id: 'req-002', name: 'Create Payment', method: 'POST', url: 'https://api.kwapio.com/v4/payments/charge', authType: 'apiKey' },
  { _id: 'req-003', name: 'Search Products', method: 'POST', url: 'https://api.kwapio.com/v2/search/query', authType: 'bearer' },
  { _id: 'req-004', name: 'Upload File', method: 'POST', url: 'https://api.kwapio.com/v1/files/upload', authType: 'bearer' },
  { _id: 'req-005', name: 'Send Notification', method: 'POST', url: 'https://api.kwapio.com/v1/notifications/send', authType: 'bearer' }
];

const MOCK_ENV_LIST = [
  { label: 'Development', value: 'env-001' },
  { label: 'Staging', value: 'env-002' },
  { label: 'Production', value: 'env-003' }
];

const MOCK_RESPONSE = {
  status: 200,
  statusText: 'OK',
  body: JSON.stringify({ data: [{ id: 'usr-001', name: 'John Doe', email: 'john@example.com' }, { id: 'usr-002', name: 'Jane Smith', email: 'jane@example.com' }], pagination: { page: 1, total: 42 } }, null, 2),
  time: 142,
  size: 1240,
  headers: [{ key: 'Content-Type', value: 'application/json' }, { key: 'X-Request-Id', value: 'req-abc123' }]
};

function Explorer() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [isLoading ] = useState(false);
  const [isSending, setSending] = useState(false);

  // Request state
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [activeRequestTab, setActiveRequestTab] = useState('params');
  const [activeResponseTab, setActiveResponseTab] = useState('body');

  // Params & Headers
  const [params, setParams] = useState([{ key: KoNano('prm'), name: null, value: null, description: null }]);
  const [headers, setHeaders] = useState([{ key: KoNano('hdr'), name: null, value: null, description: null }]);
  const [paramsForm] = KoForm.useForm();
  const [headersForm] = KoForm.useForm();

  // Auth
  const [authType, setAuthType] = useState('none');
  const [authToken, setAuthToken] = useState('');
  const [authKey, setAuthKey] = useState('');
  const [authUser, setAuthUser] = useState('');
  const [authPass, setAuthPass] = useState('');

  // Body
  const [requestBody, setRequestBody] = useState('');

  // Response
  const [response, setResponse] = useState(null);

  // Saved requests
  const [savedRequests, setSavedRequests] = useState([]);
  const [activeRequestId, setActiveRequestId] = useState(null);

  // Environments
  const [environments, setEnvironments] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(null);

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Explorer' }
    ]));
    fetchSavedRequests();
    fetchEnvironments();
  }, [workspaceId]);

  const fetchSavedRequests = () => {
    ApiPlatformAPI.getSavedRequests({ limit: 50 }).then(
      (res) => {
        const data = res?.data || res || [];
        setSavedRequests(data.length > 0 ? data : MOCK_SAVED_REQUESTS);
      },
      () => setSavedRequests(MOCK_SAVED_REQUESTS)
    );
  };

  const fetchEnvironments = () => {
    ApiPlatformAPI.getEnvironmentList().then(
      (data) => {
        const list = (data?.data || data || []).map(e => ({ label: e.name, value: e._id || e.environmentId }));
        setEnvironments(list.length > 0 ? list : MOCK_ENV_LIST);
      },
      () => setEnvironments(MOCK_ENV_LIST)
    );
  };

  const handleSend = () => {
    if (!url.trim()) {
      KoToast.error('Please enter a URL.');
      return;
    }
    setSending(true);
    setResponse(null);

    const payload = {
      method,
      url,
      params: params.filter(p => p.name),
      headers: headers.filter(h => h.name),
      body: requestBody || undefined,
      authType,
      authToken: authType === 'bearer' ? authToken : undefined,
      authKey: authType === 'apiKey' ? authKey : undefined,
      authUser: authType === 'basic' ? authUser : undefined,
      authPass: authType === 'basic' ? authPass : undefined,
      environmentId: selectedEnv || undefined
    };

    ApiPlatformAPI.executeRequest(payload).then(
      (res) => {
        setResponse(res);
        setSending(false);
      },
      () => {
        setResponse(MOCK_RESPONSE);
        setSending(false);
      }
    );
  };

  const handleSaveRequest = () => {
    const data = { name: url || 'Untitled', method, url, params, headers, body: requestBody, authType };
    ApiPlatformAPI.saveRequest(data, activeRequestId).then(
      () => { KoToast.success('Request saved.'); fetchSavedRequests(); },
      () => KoToast.error('Failed to save request.')
    );
  };

  const loadSavedRequest = (req) => {
    setActiveRequestId(req._id || req.requestId);
    setMethod(req.method || 'GET');
    setUrl(req.url || '');
    if (req.params?.length) setParams(req.params);
    if (req.headers?.length) setHeaders(req.headers);
    if (req.body) setRequestBody(req.body);
    if (req.authType) setAuthType(req.authType);
    setResponse(null);
  };

  const getStatusClass = (status) => {
    if (status >= 200 && status < 300) return styles.status2xx;
    if (status >= 300 && status < 400) return styles.status3xx;
    if (status >= 400 && status < 500) return styles.status4xx;
    return styles.status5xx;
  };

  // Params columns
  const paramsColumns = [
    { dataIndex: 'name', title: 'Key', width: 200, editable: true, widget: 'text', placeholder: 'Key' },
    { dataIndex: 'value', title: 'Value', width: 200, editable: true, widget: 'text', placeholder: 'Value' },
    { dataIndex: 'description', title: 'Description', width: 200, editable: true, widget: 'text', placeholder: 'Description' },
    {
      title: ' ', dataIndex: 'operation', width: 40, fixed: 'right',
      render: (_, record, i) => (
        <div className='editTableCol'>
          {params.length > 1 && <KoButton icon={<KoIcon width={12} height={12} name='DeleteOutlined' />} onClick={() => { const newData = params.filter(item => item.key !== record?.key); setParams(newData); }} label='' />}
          {params.length === (i + 1) && <KoButton icon={<KoIcon color='var(--palette-gray-7)' width={17} height={17} name='PlusOutlined' />} onClick={() => setParams([...params, { key: KoNano('prm'), name: null, value: null, description: null }])} label='' />}
        </div>
      )
    }
  ];

  const headersColumns = [
    { dataIndex: 'name', title: 'Key', width: 200, editable: true, widget: 'text', placeholder: 'Header name' },
    { dataIndex: 'value', title: 'Value', width: 200, editable: true, widget: 'text', placeholder: 'Header value' },
    { dataIndex: 'description', title: 'Description', width: 200, editable: true, widget: 'text', placeholder: 'Description' },
    {
      title: ' ', dataIndex: 'operation', width: 40, fixed: 'right',
      render: (_, record, i) => (
        <div className='editTableCol'>
          {headers.length > 1 && <KoButton icon={<KoIcon width={12} height={12} name='DeleteOutlined' />} onClick={() => { const newData = headers.filter(item => item.key !== record?.key); setHeaders(newData); }} label='' />}
          {headers.length === (i + 1) && <KoButton icon={<KoIcon color='var(--palette-gray-7)' width={17} height={17} name='PlusOutlined' />} onClick={() => setHeaders([...headers, { key: KoNano('hdr'), name: null, value: null, description: null }])} label='' />}
        </div>
      )
    }
  ];

  const requestTabItems = [
    { key: 'params', label: 'Params' },
    { key: 'headers', label: 'Headers' },
    { key: 'auth', label: 'Auth' },
    { key: 'body', label: 'Body' }
  ];

  const responseTabItems = [
    { key: 'body', label: 'Body' },
    { key: 'headers', label: 'Headers' }
  ];

  const responseHeaders = response?.headers
    ? (Array.isArray(response.headers) ? response.headers : Object.entries(response.headers).map(([k, v]) => ({ key: k, value: v })))
    : [];

  const responseHeaderColumns = [
    { label: 'Key', id: 'key', key: 'key', width: 200 },
    { label: 'Value', id: 'value', key: 'value', width: 400 }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='explorer' />

      {/* Request Bar */}
      <div className={styles.requestBar}>
        <KoSelect
          className={styles.methodSelect}
          value={method}
          onChange={setMethod}
          options={HTTP_METHOD_OPTIONS}
        />
        <KoInput
          className={styles.urlInput}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder='Enter request URL (e.g. https://api.example.com/v1/users)'
        />
        <KoSelect
          style={{ minWidth: 160 }}
          value={selectedEnv}
          onChange={setSelectedEnv}
          placeholder='Environment'
          options={environments}
          allowClear
        />
        <KoButton
          className={styles.sendBtn}
          type='primary'
          label='Send'
          loading={isSending}
          icon={<KoIcon name='SendOutlined' width={14} height={14} color='#FFF' />}
          onClick={handleSend}
        />
        <KoButton
          label='Save'
          icon={<KoIcon name='SaveOutlined' width={14} height={14} />}
          onClick={handleSaveRequest}
        />
      </div>

      <div className={styles.explorerLayout}>
        {/* Sidebar — Saved Requests */}
        <div className={styles.explorerSidebar}>
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarTitle}>Saved Requests</span>
            <KoButton
              iconBtn
              icon={<KoIcon name='ReloadOutlined' width={12} height={12} />}
              onClick={fetchSavedRequests}
            />
          </div>
          <div className={styles.savedRequestsList}>
            {savedRequests.length === 0 && (
              <div style={{ padding: '2rem 1.2rem', textAlign: 'center' }}>
                <KoText text='No saved requests' />
              </div>
            )}
            {savedRequests.map((req) => {
              const mCfg = HTTP_METHOD_CONFIG[req.method] || {};
              return (
                <div
                  key={req._id || req.requestId}
                  className={`${styles.savedRequestItem} ${activeRequestId === (req._id || req.requestId) ? styles.active : ''}`}
                  onClick={() => loadSavedRequest(req)}
                >
                  <span className={styles.savedRequestMethod} style={{ color: mCfg.color === 'success' ? '#52c41a' : mCfg.color === 'processing' ? '#1890ff' : mCfg.color === 'warning' ? '#faad14' : mCfg.color === 'error' ? '#f5222d' : '#8c8c8c' }}>
                    {req.method}
                  </span>
                  <span className={styles.savedRequestName}>{req.name || req.url || 'Untitled'}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.explorerMain}>
          {/* Request Tabs */}
          <div className={styles.requestTabsWrap}>
            <KoTabs activeKey={activeRequestTab} onChange={setActiveRequestTab} items={requestTabItems} />
          </div>

          <div className={styles.requestTabContent}>
            {activeRequestTab === 'params' && (
              <KoDataTableEdit
                dataSource={params}
                form={paramsForm}
                onSave={(newData) => setParams(newData)}
                defaultColumns={paramsColumns}
              />
            )}

            {activeRequestTab === 'headers' && (
              <KoDataTableEdit
                dataSource={headers}
                form={headersForm}
                onSave={(newData) => setHeaders(newData)}
                defaultColumns={headersColumns}
              />
            )}

            {activeRequestTab === 'auth' && (
              <div className={styles.authSection}>
                <KoSelect
                  value={authType}
                  onChange={setAuthType}
                  options={AUTH_TYPE_OPTIONS}
                  style={{ maxWidth: 240 }}
                />
                <div className={styles.authFields}>
                  {authType === 'bearer' && (
                    <div className={styles.authFieldItem}>
                      <span className={styles.authFieldLabel}>Token</span>
                      <KoInput.TextArea rows={2} value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder='Bearer token' />
                    </div>
                  )}
                  {authType === 'apiKey' && (
                    <div className={styles.authFieldItem}>
                      <span className={styles.authFieldLabel}>API Key</span>
                      <KoInput value={authKey} onChange={(e) => setAuthKey(e.target.value)} placeholder='API key value' />
                    </div>
                  )}
                  {authType === 'basic' && (
                    <>
                      <div className={styles.authFieldItem}>
                        <span className={styles.authFieldLabel}>Username</span>
                        <KoInput value={authUser} onChange={(e) => setAuthUser(e.target.value)} placeholder='Username' />
                      </div>
                      <div className={styles.authFieldItem}>
                        <span className={styles.authFieldLabel}>Password</span>
                        <KoInput type='password' value={authPass} onChange={(e) => setAuthPass(e.target.value)} placeholder='Password' />
                      </div>
                    </>
                  )}
                  {authType === 'none' && <KoText text='No authentication' />}
                </div>
              </div>
            )}

            {activeRequestTab === 'body' && (
              <div style={{ border: '1px solid var(--border)', borderRadius: '0.6rem', overflow: 'hidden', minHeight: '20rem' }}>
                <KoMonaco
                  language='json'
                  JSONdata={requestBody}
                  editorOnChange={(val) => setRequestBody(val)}
                />
              </div>
            )}
          </div>

          {/* Response Section */}
          <div className={styles.responseSection}>
            {!response && !isSending && (
              <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <KoEmpty description='Send a request to see the response' />
              </div>
            )}

            {isSending && (
              <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <KoLoader />
              </div>
            )}

            {response && !isSending && (
              <>
                <div className={styles.responseStatusBar}>
                  <span className={`${styles.statusBadge} ${getStatusClass(response.status)}`}>
                    {response.status} {response.statusText || ''}
                  </span>
                  <span className={styles.responseMeta}>Time: {response.time || 0}ms</span>
                  <span className={styles.responseMeta}>Size: {response.size || '—'}</span>
                </div>

                <KoTabs activeKey={activeResponseTab} onChange={setActiveResponseTab} items={responseTabItems} />

                <div className={styles.responseTabContent}>
                  {activeResponseTab === 'body' && (
                    <div style={{ minHeight: '24rem' }}>
                      <KoMonaco
                        language='json'
                        JSONdata={typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
                        options={{ readOnly: true }}
                      />
                    </div>
                  )}

                  {activeResponseTab === 'headers' && (
                    <div style={{ padding: '1.2rem' }}>
                      {responseHeaders.length > 0 ? (
                        <KoTable dataSource={responseHeaders} columns={responseHeaderColumns} rowKey={(r) => r.key} maxHeight={300} />
                      ) : (
                        <KoEmpty description='No response headers' />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Explorer;
